import React, { useState, useEffect } from 'react';
import { Plus, Search, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { getUseCases, createUseCase, updateUseCase, deleteUseCase } from '../api/categories';
import CategoryModal from '../components/CategoryModal';
import CategoryCard from '../components/CategoryCard';
import DeleteConfirmModal from '../components/DeleteConfirmModal';

const UseCasesPage = () => {
  const [useCases, setUseCases] = useState([]);
  const [filteredUseCases, setFilteredUseCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUseCase, setSelectedUseCase] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch use cases
  const fetchUseCases = async () => {
    setLoading(true);
    try {
      const data = await getUseCases();
      setUseCases(data);
      setFilteredUseCases(data);
    } catch (error) {
      toast.error('Failed to fetch use cases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUseCases();
  }, []);

  // Search filter
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUseCases(useCases);
    } else {
      const filtered = useCases.filter((useCase) =>
        useCase.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUseCases(filtered);
    }
  }, [searchTerm, useCases]);

  // Handle create/update with optimistic UI
  const handleSubmit = async (data) => {
    try {
      if (selectedUseCase) {
        const updated = await updateUseCase(selectedUseCase.id, data);
        setUseCases((prev) =>
          prev.map((u) => (u.id === selectedUseCase.id ? { ...u, ...updated } : u))
        );
        toast.success('Use case updated successfully');
      } else {
        const created = await createUseCase(data);
        setUseCases((prev) => [...prev, created]);
        toast.success('Use case created successfully');
      }
      setShowModal(false);
      setSelectedUseCase(null);
    } catch (error) {
      throw error;
    }
  };

  // Handle edit
  const handleEdit = (useCase) => {
    setSelectedUseCase(useCase);
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = (useCase) => {
    setSelectedUseCase(useCase);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await deleteUseCase(selectedUseCase.id);
      setUseCases((prev) => prev.filter((u) => u.id !== selectedUseCase.id));
      toast.success('Use case deleted successfully');
      setShowDeleteModal(false);
      setSelectedUseCase(null);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to delete use case';
      toast.error(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary-offWhite p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-primary-black">Use Case Management</h1>
              <p className="text-gray-600 mt-1">Manage tile use cases and applications</p>
            </div>
            <button
              onClick={() => {
                setSelectedUseCase(null);
                setShowModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add Use Case
            </button>
          </div>

          {/* Search and Actions */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search use cases..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
              />
            </div>
            <button
              onClick={fetchUseCases}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6 border-2 border-accent-gold">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Total Use Cases</h3>
            <p className="text-3xl font-bold text-primary-black">{useCases.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6 border-2 border-gray-400">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Showing</h3>
            <p className="text-3xl font-bold text-primary-black">{filteredUseCases.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6 border-2 border-gray-400">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Search Results</h3>
            <p className="text-3xl font-bold text-primary-black">
              {searchTerm ? filteredUseCases.length : '-'}
            </p>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-gold"></div>
          </div>
        ) : filteredUseCases.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500 text-lg">
              {searchTerm
                ? 'No use cases found matching your search'
                : 'No use cases yet. Add your first use case!'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredUseCases.map((useCase) => (
              <CategoryCard
                key={useCase.id}
                category={useCase}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <CategoryModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedUseCase(null);
        }}
        onSubmit={handleSubmit}
        initialData={selectedUseCase}
        title={selectedUseCase ? 'Edit Use Case' : 'Add New Use Case'}
        categoryType="use_case"
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedUseCase(null);
        }}
        onConfirm={confirmDelete}
        itemName={selectedUseCase?.name}
        loading={deleteLoading}
      />
    </div>
  );
};

export default UseCasesPage;
